'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  category: 'milestone' | 'certification' | 'expansion' | 'innovation';
}

interface CompanyTimelineProps {
  className?: string;
}

const CompanyTimeline = ({ className = '' }: CompanyTimelineProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const timelineEvents: TimelineEvent[] = [
    {
      year: '2008',
      title: 'Company Founded',
      description: 'Techaero established with mission to revolutionize aerospace manufacturing through precision and innovation.',
      category: 'milestone'
    },
    {
      year: '2010',
      title: 'AS9100 Certification',
      description: 'Achieved AS9100 certification, establishing foundation for aerospace quality excellence.',
      category: 'certification'
    },
    {
      year: '2012',
      title: 'ITAR Registration',
      description: 'Obtained ITAR registration, enabling defense and military aerospace manufacturing capabilities.',
      category: 'certification'
    },
    {
      year: '2014',
      title: 'First Major Satellite Program',
      description: 'Delivered critical components for commercial satellite constellation, establishing space industry presence.',
      category: 'milestone'
    },
    {
      year: '2015',
      title: 'NADCAP Accreditation',
      description: 'Earned NADCAP accreditation for special processes, demonstrating advanced manufacturing capabilities.',
      category: 'certification'
    },
    {
      year: '2016',
      title: 'Huntsville Facility Opening',
      description: 'Expanded operations with new 85,000 sq ft precision manufacturing facility in Alabama.',
      category: 'expansion'
    },
    {
      year: '2018',
      title: 'Defense Clearance Obtained',
      description: 'Secured Secret facility clearance, enabling classified defense manufacturing programs.',
      category: 'certification'
    },
    {
      year: '2019',
      title: 'Additive Manufacturing Lab',
      description: 'Launched advanced additive manufacturing capabilities with metal 3D printing technology.',
      category: 'innovation'
    },
    {
      year: '2021',
      title: 'Colorado Springs Expansion',
      description: 'Opened dedicated defense systems production facility with 120,000 sq ft capacity.',
      category: 'expansion'
    },
    {
      year: '2022',
      title: '500th Aerospace Project',
      description: 'Milestone achievement delivering 500th successful aerospace manufacturing project.',
      category: 'milestone'
    },
    {
      year: '2023',
      title: 'Digital Platform Launch',
      description: 'Introduced comprehensive digital manufacturing platform for real-time project management.',
      category: 'innovation'
    },
    {
      year: '2024',
      title: 'Industry Leadership',
      description: 'Recognized as leading aerospace manufacturing partner with 50+ active manufacturing partnerships.',
      category: 'milestone'
    }
  ];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'milestone':
        return 'FlagIcon';
      case 'certification':
        return 'ShieldCheckIcon';
      case 'expansion':
        return 'BuildingOfficeIcon';
      case 'innovation':
        return 'LightBulbIcon';
      default:
        return 'StarIcon';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'milestone':
        return 'text-mission-orange';
      case 'certification':
        return 'text-aerospace-green';
      case 'expansion':
        return 'text-accent';
      case 'innovation':
        return 'text-accent';
      default:
        return 'text-foreground';
    }
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 lg:py-28 bg-background ${className}`}>
        <div className="mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
                <span className="font-cta text-xs text-accent tracking-wider">OUR JOURNEY</span>
              </div>
              <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
                15+ Years of Aerospace Excellence
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>
              <div className="space-y-12">
                {timelineEvents.map((event, index) => (
                  <div key={index} className="relative pl-20">
                    <div className="absolute left-0 w-16 h-16 bg-card border-2 border-accent rounded-full flex items-center justify-center">
                      <span className="font-accent text-sm font-bold text-accent">{event.year}</span>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h3 className="font-headline text-lg font-bold text-foreground mb-2">
                        {event.title}
                      </h3>
                      <p className="font-body text-sm text-technical-gray">
                        {event.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 lg:py-28 bg-background ${className}`}>
      <div className="mx-auto px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
              <span className="font-cta text-xs text-accent tracking-wider">OUR JOURNEY</span>
            </div>
            <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
              15+ Years of Aerospace Excellence
            </h2>
            <p className="font-body text-lg text-technical-gray max-w-3xl mx-auto">
              From our founding vision to becoming a trusted aerospace manufacturing partner, our journey reflects continuous innovation, unwavering quality, and mission-critical commitment.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent via-mission-orange to-accent"></div>

            <div className="space-y-12">
              {timelineEvents.map((event, index) => (
                <div key={index} className="relative pl-20 group">
                  <div className="absolute left-0 w-16 h-16 bg-card border-2 border-accent rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:shadow-glow">
                    <span className="font-accent text-sm font-bold text-accent">{event.year}</span>
                  </div>

                  <div className="bg-card border border-border rounded-xl p-6 hover:border-accent transition-all duration-300 hover:shadow-glow">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-headline text-lg font-bold text-foreground group-hover:text-accent transition-colors duration-300">
                        {event.title}
                      </h3>
                      <Icon
                        name={getCategoryIcon(event.category) as any}
                        size={20}
                        className={`${getCategoryColor(event.category)} flex-shrink-0`}
                      />
                    </div>
                    <p className="font-body text-sm text-technical-gray leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;